import { useCallback, useId, useMemo, useState } from 'react';

import BallotOutlinedIcon from '@mui/icons-material/BallotOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import {
  Box,
  ButtonBase,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography
} from '@mui/material';
import { DateRangeIcon } from '@mui/x-date-pickers';
import { format, isValid } from 'date-fns';
import { usePostHog } from 'posthog-js/react';
import { useNavigate } from 'react-router';

import ChartBoxMultipleOutline from '@/assets/icons/ChartBoxMultipleOutline';
import Button from '@/components/Buttons/Button';
import { ChartProvider } from '@/components/Chart/context/ChartContext';
import { exportChartImage } from '@/components/Chart/utils/exportChartImage';
import useDateText, { DateTextVariants } from '@/components/DateText/useDateText';
import { NameInput } from '@/components/NameInput';
import { EXPORT_NAME_MAX_LENGTH } from '@/constants/name';
import { DataHubCard } from '@/features/DataHub/components/DataHubCard';
import { DataHubHeader } from '@/features/DataHub/components/DataHubHeader';
import { DataHubSection } from '@/features/DataHub/components/DataHubSection';
import { AccordionProvider } from '@/features/DataHub/components/ExportDrawer/AccordionContext';
import PreviewCustomChart from '@/features/DataHub/components/Preview/ChartPreviews/PreviewCustomChart';
import { DATAHUB_SPACING_LG } from '@/features/DataHub/constants/ui';
import { useDataHubUserConfig } from '@/features/DataHub/hooks/useDataHubUserConfig';
import { DateRangePickers } from '@/features/DataHub/modules/Export/components/ExportSettings/DateRangePickers';
import NoDataDateAlert from '@/features/DataHub/modules/Export/components/ExportSettings/NoDataDateAlert';
import { RangePeriodFields } from '@/features/DataHub/modules/Export/components/ExportSettings/RangePeriodFields';
import { MIN_DATE } from '@/features/DataHub/modules/Export/constants/dateSettings';
import { validateExportNameWithMaxLength } from '@/features/DataHub/modules/Export/services/ExportNameService';
import {
  DateRangeType,
  type ExportSettingsType
} from '@/features/DataHub/modules/Export/types/context.types';
import {
  getDateRangeValidation,
  getRelativeRangeValue,
  parseSelectedRange,
  sanitizePeriodNumber
} from '@/features/DataHub/modules/Export/utils';
import { useAppDispatch, useAppSelector } from '@/hooks/redux-hooks';
import { closeDrawer } from '@/slices/drawerSlice';
import { deepMerge } from '@/utils/deepMerge';
import { buildDisplayDateSuffix } from '@/utils/exportFileDateSuffix';
import { parseFromToISO } from '@/utils/parseFromToISO';

import {
  buildCustomChartExportFileName,
  buildCustomChartInitialExportName
} from './customChartExportName';

import type { Chart, ExportingMimeTypeValue, Options } from 'highcharts';

const EXPORT_FONT_FAMILY = `'Cairo', Arial, sans-serif`;
const CAIRO_FONT_URL = '/fonts/Cairo.ttf';

const periodLabels: Record<string, string> = {
  d: 'Days',
  m: 'Months',
  y: 'Years'
};

const IMAGE_FORMATS: { label: string; type: ExportingMimeTypeValue }[] = [
  { label: 'PNG', type: 'image/png' },
  { label: 'PDF', type: 'application/pdf' },
  { label: 'JPEG', type: 'image/jpeg' },
  { label: 'SVG', type: 'image/svg+xml' }
];

const CustomChartExportViewInner = ({
  metadata,
  tabName,
  selectedRange
}: {
  metadata: Record<string, object | string>;
  tabName: string;
  selectedRange?: string;
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const postHog = usePostHog();
  const [chartInstance, setChartInstance] = useState<Chart | null>(null);
  const { formatDateString } = useDateText();
  const { timezoneLabel, dateFormatValue } = useDataHubUserConfig();
  const radioGroupId = useId();
  const getDateSuffix = useCallback(() => {
    const nowIso = new Date().toJSON();
    const date = formatDateString(nowIso, DateTextVariants.FULL);
    return buildDisplayDateSuffix(date, nowIso);
  }, [formatDateString]);

  const [exportName, setExportName] = useState(() => buildCustomChartInitialExportName(tabName));
  const [selectedFormat, setSelectedFormat] = useState<ExportingMimeTypeValue>('image/png');
  const [hasPreviewData, setHasPreviewData] = useState<boolean | null>(null);

  const parsedInitialRange = useMemo(() => parseSelectedRange(selectedRange), [selectedRange]);
  const [dateRangeType, setDateRangeType] = useState<DateRangeType>(
    () => parsedInitialRange.dateRangeType
  );
  const [periodNumber, setPeriodNumber] = useState(() => parsedInitialRange.periodNumber);
  const [period, setPeriod] = useState<ExportSettingsType['period']>(
    () => parsedInitialRange.period
  );
  const [fromDate, setFromDate] = useState<Date | null>(() => parsedInitialRange.from);
  const [toDate, setToDate] = useState<Date | null>(() => parsedInitialRange.to);
  const exportNameError = useMemo(
    () => validateExportNameWithMaxLength(exportName, EXPORT_NAME_MAX_LENGTH),
    [exportName]
  );

  const fromISO = useMemo(() => {
    switch (dateRangeType) {
      case DateRangeType.ALL:
        return undefined;
      case DateRangeType.RANGE: {
        const from = parseFromToISO(getRelativeRangeValue(periodNumber, period));
        const fromDateValue = new Date(from);
        return Number.isNaN(fromDateValue.getTime()) ? undefined : fromDateValue.toISOString();
      }
      case DateRangeType.DATES:
        return fromDate?.toISOString();
      default:
        return undefined;
    }
  }, [dateRangeType, periodNumber, period, fromDate]);

  const toISO = useMemo(() => {
    if (dateRangeType !== DateRangeType.DATES) return undefined;
    return toDate?.toISOString();
  }, [dateRangeType, toDate]);

  const collapsedSummary = useMemo(() => {
    const tz = `Timezone: ${timezoneLabel}`;
    switch (dateRangeType) {
      case DateRangeType.ALL:
        return `All, ${tz}`;
      case DateRangeType.RANGE:
        return `Last ${periodNumber} ${periodLabels[period] ?? period}, ${tz}`;
      case DateRangeType.DATES: {
        const fromStr = fromDate && isValid(fromDate) ? format(fromDate, dateFormatValue) : '';
        const toStr = toDate && isValid(toDate) ? format(toDate, dateFormatValue) : '';
        return `${fromStr} - ${toStr}, ${tz}`;
      }
      default:
        return '';
    }
  }, [dateRangeType, periodNumber, period, fromDate, toDate, timezoneLabel, dateFormatValue]);

  const onChangeDateRangeType = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value as DateRangeType;
    setDateRangeType(value);
  }, []);

  const validation = useMemo(
    () => getDateRangeValidation({ dateRangeType, from: fromDate, to: toDate, periodNumber }),
    [dateRangeType, fromDate, toDate, periodNumber]
  );
  const hasDateSettingsError =
    validation.periodNumberError || validation.fromError || validation.toError;
  const showNoDataDateAlert = hasPreviewData === false && !hasDateSettingsError;

  const handleExport = useCallback(() => {
    if (!chartInstance || exportNameError || hasDateSettingsError) return;

    const dateSuffix = getDateSuffix();
    const fileName = buildCustomChartExportFileName(exportName, dateSuffix);
    const baseChartOptions = chartInstance.options.exporting?.chartOptions ?? chartInstance.options;
    const exportOptions = deepMerge({} as Options, baseChartOptions);
    exportOptions.chart = {
      ...exportOptions.chart,
      style: {
        ...exportOptions.chart?.style,
        fontFamily: EXPORT_FONT_FAMILY
      }
    };
    exportOptions.legend = {
      ...exportOptions.legend
    };

    const exportingOptions =
      selectedFormat === 'application/pdf'
        ? {
            local: true,
            pdfFont: {
              normal: CAIRO_FONT_URL,
              bold: CAIRO_FONT_URL,
              italic: CAIRO_FONT_URL,
              bolditalic: CAIRO_FONT_URL
            }
          }
        : undefined;

    postHog?.capture('chart_download_button_click', { format: selectedFormat, tabName });

    exportChartImage({
      chart: chartInstance,
      type: selectedFormat,
      filename: fileName,
      options: exportOptions,
      exportingOptions
    });
  }, [
    chartInstance,
    exportName,
    exportNameError,
    getDateSuffix,
    hasDateSettingsError,
    postHog,
    selectedFormat,
    tabName
  ]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        justifyContent: 'space-between'
      }}
    >
      <DataHubSection
        scroll
        gap={DATAHUB_SPACING_LG}
        sx={{ p: DATAHUB_SPACING_LG }}
      >
        <DataHubHeader
          subtitle='Set up your export file'
          title='Export configuration'
        />
        <AccordionProvider>
          <DataHubCard
            expandable={false}
            title={
              <>
                <Box
                  component='span'
                  sx={{
                    display: 'inline-flex',
                    width: 'fit-content',
                    '& .name-input': { flex: 'none', width: 'fit-content' },
                    '& .name-input .MuiInputBase-formControl': { width: 'auto' },
                    '& .name-input .MuiInputBase-input': {
                      fieldSizing: 'content',
                      minWidth: '14ch',
                      maxWidth: `${EXPORT_NAME_MAX_LENGTH + 1}ch`
                    }
                  }}
                >
                  <NameInput
                    error={exportNameError ?? undefined}
                    isEmpty={exportName.trim().length === 0}
                    maxLength={EXPORT_NAME_MAX_LENGTH}
                    size={18}
                    value={exportName}
                    onChange={setExportName}
                  />
                </Box>
                <EditOutlinedIcon sx={{ fontSize: '16px' }} />
              </>
            }
          >
            {null}
          </DataHubCard>

          <DataHubCard
            expandable
            collapsedSummary={collapsedSummary}
            icon={<DateRangeIcon sx={{ fontSize: '16px' }} />}
            subtitle='Select date range and timezone options'
            title='Date settings'
          >
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                fontSize: '14px',
                my: 1.5
              }}
            >
              <Divider sx={{ mb: 2 }} />
              {showNoDataDateAlert && <NoDataDateAlert />}
              <Typography sx={{ mb: 1.5, fontSize: 14 }}>Date settings</Typography>
              <FormControl>
                <RadioGroup
                  aria-labelledby={radioGroupId}
                  value={dateRangeType}
                  onChange={onChangeDateRangeType}
                >
                  <FormControlLabel
                    control={<Radio />}
                    label='All'
                    value={DateRangeType.ALL}
                  />
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      height: 50,
                      marginBottom: validation.periodNumberError ? 2 : 0
                    }}
                  >
                    <FormControlLabel
                      control={<Radio />}
                      label='Last'
                      value={DateRangeType.RANGE}
                    />
                    <RangePeriodFields
                      period={period}
                      periodNumber={periodNumber}
                      periodNumberError={validation.periodNumberError}
                      onChangePeriod={e => {
                        setPeriod(e.target.value);
                      }}
                      onChangePeriodNumber={e => {
                        setPeriodNumber(sanitizePeriodNumber(e.target.value));
                      }}
                    />
                  </Box>
                  <FormControlLabel
                    control={<Radio />}
                    label='From'
                    value={DateRangeType.DATES}
                  />
                  <DateRangePickers
                    dateFormat={dateFormatValue}
                    from={fromDate}
                    fromError={validation.fromError}
                    fromErrorMessage={validation.fromErrorMessage}
                    isDatePickerDisabled={dateRangeType !== DateRangeType.DATES}
                    maxDate={new Date()}
                    minDate={MIN_DATE}
                    to={toDate}
                    toError={validation.toError}
                    toErrorMessage={validation.toErrorMessage}
                    onChange={(field, value) => {
                      if (field === 'from') setFromDate(value);
                      else setToDate(value);
                    }}
                  />
                </RadioGroup>
              </FormControl>
              <Divider sx={{ my: 2 }} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'baseline'
                }}
              >
                Timezone: {timezoneLabel}{' '}
                <ButtonBase
                  sx={{ ml: 2, color: 'primary.main' }}
                  onClick={() => {
                    navigate('/profile', { replace: true });
                    dispatch(closeDrawer());
                  }}
                >
                  Change in Settings
                </ButtonBase>
              </Box>
              <Divider sx={{ mt: 2 }} />
            </Box>
          </DataHubCard>

          <DataHubCard
            expandable={false}
            icon={<ChartBoxMultipleOutline sx={{ fontSize: '16px' }} />}
            subtitle='Preview visualization before exporting'
            title='Preview'
          >
            <PreviewCustomChart
              from={fromISO}
              metadata={metadata}
              to={toISO}
              onChartReady={setChartInstance}
              onHasDataChange={setHasPreviewData}
            />
          </DataHubCard>

          <DataHubCard
            expandable={false}
            icon={<BallotOutlinedIcon sx={{ fontSize: '16px' }} />}
            subtitle='Select image format'
            title='Format'
          >
            <Box
              sx={{
                maxHeight: 150,
                fontSize: 16,
                fontWeight: 600,
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                flexWrap: 'wrap'
              }}
            >
              {IMAGE_FORMATS.map(({ label, type }) => (
                <Box
                  key={label}
                  sx={{ mt: 1 }}
                >
                  <ButtonBase
                    sx={{
                      border: '1px solid',
                      borderColor: selectedFormat === type ? 'white' : 'transparent',
                      backgroundColor: 'secondary.main',
                      justifyContent: 'flex-start',
                      px: 1,
                      py: 0.5,
                      width: '100%'
                    }}
                    onClick={() => {
                      setSelectedFormat(type);
                    }}
                  >
                    {label}
                  </ButtonBase>
                </Box>
              ))}
            </Box>
          </DataHubCard>
        </AccordionProvider>
      </DataHubSection>

      <Box
        sx={{
          minHeight: '96px',
          width: '100%',
          backgroundColor: 'background.main',
          borderTop: '1px solid',
          borderColor: 'info.dark',
          p: DATAHUB_SPACING_LG,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: DATAHUB_SPACING_LG,
          '& > *': { flex: 1 }
        }}
      >
        <Button
          sx={{ color: 'primary.main', borderColor: 'primary.main' }}
          variant='outlined'
          onClick={() => dispatch(closeDrawer())}
        >
          Cancel
        </Button>
        <Button
          disabled={
            !chartInstance ||
            hasPreviewData !== true ||
            Boolean(exportNameError) ||
            hasDateSettingsError
          }
          variant='contained'
          onClick={handleExport}
        >
          Export image
        </Button>
      </Box>
    </Box>
  );
};

export const CustomChartExportView = () => {
  const customChartExportContext = useAppSelector(state => state.drawer.customChartExportContext);

  if (!customChartExportContext) return null;

  return (
    <ChartProvider>
      <CustomChartExportViewInner
        metadata={customChartExportContext.metadata}
        selectedRange={customChartExportContext.selectedRange}
        tabName={customChartExportContext.tabName}
      />
    </ChartProvider>
  );
};
